import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import { IconButton, Tooltip, ListSubheader, ListSubheaderProps } from "@mui/material";
import { gql } from "generated-graphql";
import {
  LepcPickerFragment,
  GroupedLepcSearchQuery,
  GroupedLepcSearchQueryVariables,
} from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";

import { DisplayOnlyField } from "../../../../../../components/Forms/DisplayOnlyField";
import GroupedAsyncAutocomplete from "../../../../../../components/Forms/GroupedAsyncAutocomplete";

// Base props for the grouped LEPC picker
type GroupedLepcPickerProps = {
  facilityIds: string[];
  onChange?: (value: GroupedLepc | null) => void;
  value?: GroupedLepc | null;
  disabled?: boolean;
  helperText?: string | React.JSX.Element;
  displayAgencyLink?: boolean;
} & ControllerFieldState;

// Extended LEPC type with grouping information
export type GroupedLepc = LepcPickerFragment & {
  group: "primary" | "supplemental";
  isPrimary: boolean;
};

const GROUPED_LEPCS_SEARCH = gql(`
  query GroupedLepcSearch($facilityIds: [String!]!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    groupedLepcSearch(facilityIds: $facilityIds, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      primary {
        ...LepcPicker
      }
      supplemental {
        ...LepcPicker
      }
      primaryCount
      supplementalCount
    }
  }
`);

// Shared function to format LEPC option labels
const getLepcOptionLabel = (lepc: GroupedLepc) => {
  let label = `${lepc.name} ${lepc.city}, ${lepc.state}`;
  if (lepc.county) {
    label = `${lepc.name} ${lepc.city}, ${lepc.county}, ${lepc.state}`;
  }
  return label;
};

// Custom ListSubheader for LEPC groups
function LepcGroupHeader(props: ListSubheaderProps) {
  return (
    <ListSubheader
      {...props}
      sx={{
        fontWeight: "bold",
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    />
  );
}
LepcGroupHeader.muiSkipListHighlight = true;

// Facility-based grouped picker with primary and supplemental LEPCs
export const GroupedLepcPickerWithFacilityFilter = forwardRef<
  HTMLDivElement,
  GroupedLepcPickerProps
>(function GroupedLepcPickerWithFacilityFilter(
  { facilityIds, onChange, value, error, disabled, helperText, displayAgencyLink },
  ref
) {
  const formContext = useValidatingFormContext();

  // Transform query response into grouped format
  const getGroupedItems = (data: GroupedLepcSearchQuery): GroupedLepc[] => {
    const primaryItems: GroupedLepc[] = data.groupedLepcSearch.primary.map((lepc) => ({
      ...lepc,
      group: "primary" as const,
      isPrimary: true,
    }));

    const supplementalItems: GroupedLepc[] = data.groupedLepcSearch.supplemental.map((lepc) => ({
      ...lepc,
      group: "supplemental" as const,
      isPrimary: false,
    }));

    // Combine and deduplicate by ID
    // A LEPC might be both primary and supplemental if it's linked to multiple facilities
    // This is unlikely but possible.
    const allItems = [...primaryItems, ...supplementalItems];
    const uniqueItems = allItems.reduce((acc, item) => {
      const existing = acc.find((existing) => existing.id === item.id);
      if (!existing) {
        acc.push(item);
      } else if (item.isPrimary && !existing.isPrimary) {
        // If we have both primary and supplemental for the same LEPC, prefer primary
        const index = acc.indexOf(existing);
        acc[index] = item;
      }
      return acc;
    }, [] as GroupedLepc[]);

    return uniqueItems;
  };

  // Group by function for the autocomplete
  const groupBy = (option: GroupedLepc) => option.group;

  // Custom group renderer with friendly labels
  const renderGroup = (params: any) => (
    <li key={params.key}>
      <LepcGroupHeader>
        {params.group === "primary" ? "Primary LEPCs" : "Supplemental LEPCs"}
      </LepcGroupHeader>
      <ul style={{ padding: 0 }}>{params.children}</ul>
    </li>
  );

  // Compose facility filter for search
  const facilityFilter = facilityIds.length ? `facilityId:${facilityIds.join("|")}` : "";

  if (formContext?.metadata?.isDisplayOnly) {
    return <DisplayOnlyField text={value ? getLepcOptionLabel(value) : ""} label={"LEPC"} />;
  }

  return (
    <GroupedAsyncAutocomplete<
      GroupedLepcSearchQuery,
      GroupedLepc,
      "primary" | "supplemental",
      GroupedLepcSearchQueryVariables
    >
      query={GROUPED_LEPCS_SEARCH}
      variables={{
        facilityIds,
      }}
      getItems={getGroupedItems}
      getOptionLabel={getLepcOptionLabel}
      groupBy={groupBy}
      groupOrder={["primary", "supplemental"]}
      renderGroup={renderGroup}
      onChange={onChange}
      value={value || null}
      error={error}
      disabled={disabled}
      additionalSearchString={facilityFilter}
      helperText={helperText}
      additionalEndAdornment={displayAgencyLink && agencyLinkAdornment(value)}
      placeholderLabel="Search LEPCs"
    />
  );
});

function agencyLinkAdornment(value: GroupedLepc | null | undefined) {
  return (
    value && (
      <Tooltip title="Open LEPC">
        <IconButton
          size="small"
          sx={{
            padding: "4px",
            color: "primary.main",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the dropdown
            window.open(`/staff/agencies/lepcs?omnisearch=id:${value.id}`, "_blank");
          }}
        >
          <LaunchOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  );
}
